/**
 * DatabaseRepository - PocketBase CRUD for books collection
 */

import PocketBase from 'pocketbase';

export class DatabaseRepository {
    constructor(pocketbaseURL, proxy) {
        this.pb = new PocketBase(pocketbaseURL);
        this.proxy = proxy;
        this.collectionName = 'books';
    }

    async getAllBooks() {
        try {
            return await this.pb.collection(this.collectionName).getFullList({
                $autoCancel: false,
                batch: 500,
            });
        } catch (error) {
            if (error.message?.includes('autocancelled')) return [];
            throw error;
        }
    }

    async getBook(id) {
        return this.pb.collection(this.collectionName).getOne(id);
    }

    async findByGoogleBooksId(googleBooksId) {
        try {
            return await this.pb.collection(this.collectionName)
                .getFirstListItem(`googleBooksId="${googleBooksId}"`, { $autoCancel: false });
        } catch (error) {
            if (error.status === 404) return null;
            throw error;
        }
    }

    /**
     * Convert a base64 data URL (from server-side download) to a File object.
     */
    dataURLtoFile(dataURL, filename) {
        try {
            const [header, base64] = dataURL.split(',');
            const mime = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
            const bytes = atob(base64);
            const arr = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
            return new File([arr], filename, { type: mime });
        } catch {
            return null;
        }
    }

    async createBook(data) {
        const formData = new FormData();

        // Fields used only client-side, not part of PB schema
        const SKIP_FIELDS = ['coverDataURL', 'googleCoverURL'];

        Object.entries(data).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            if (SKIP_FIELDS.includes(key)) return;
            if (Array.isArray(value)) {
                formData.append(key, JSON.stringify(value));
            } else {
                formData.append(key, value);
            }
        });

        // Use server-downloaded cover image (base64 data URL avoids CORS)
        if (data.coverDataURL) {
            const imageFile = this.dataURLtoFile(data.coverDataURL, `cover_${data.googleBooksId}.jpg`);
            if (imageFile) formData.append('coverImage', imageFile);
        }

        return this.proxy.collection(this.collectionName).create(formData);
    }

    async updateBook(id, data) {
        return this.proxy.collection(this.collectionName).update(id, data);
    }

    async updateBookWithImage(id, data, coverDataURL) {
        const formData = new FormData();
        const SKIP_FIELDS = ['coverDataURL', 'googleCoverURL'];
        Object.entries(data).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            if (SKIP_FIELDS.includes(key)) return;
            if (Array.isArray(value)) {
                formData.append(key, JSON.stringify(value));
            } else {
                formData.append(key, value);
            }
        });
        if (coverDataURL) {
            // Remove any existing cover files before uploading the new one.
            // PocketBase file fields append on PATCH — they don't auto-replace —
            // so we must explicitly delete old files via the "-fieldname" syntax.
            try {
                const current = await this.pb.collection(this.collectionName).getOne(id, { $autoCancel: false });
                const oldFiles = Array.isArray(current.coverImage)
                    ? current.coverImage
                    : current.coverImage ? [current.coverImage] : [];
                for (const oldFile of oldFiles) {
                    formData.append('-coverImage', oldFile);
                }
            } catch { /* proceed anyway — new file will still be added */ }

            // Unique filename so PocketBase generates a fresh URL (no browser cache hit)
            const ts = Date.now();
            const imageFile = this.dataURLtoFile(coverDataURL, `cover_${id}_${ts}.jpg`);
            if (imageFile) formData.append('coverImage', imageFile);
        }
        return this.proxy.collection(this.collectionName).update(id, formData);
    }

    async deleteBook(id) {
        await this.proxy.collection(this.collectionName).delete(id);
        return true;
    }
}

export default DatabaseRepository;
